import type { TFunction } from 'i18next';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  getMetadataString,
  getMetadataStringArray,
} from '../helpers/purchase-order-activity-metadata';
import type { PurchaseOrderActivityItem } from '../types/purchase-order-activity-item';

type PurchaseOrderActivityMailSentProps = {
  activity: PurchaseOrderActivityItem;
  t: TFunction<'purchaseOrder'>;
};

function getMailSentLabel(
  eventCode: string,
  t: TFunction<'purchaseOrder'>,
): string {
  switch (eventCode) {
    case 'production_declaration_requested':
      return t('activity.mail.productionDeclarationRequested');
    case 'production_declaration_approve_email_sent':
      return t('activity.mail.productionDeclarationApproveSent');
    case 'production_declaration_refuse_email_sent':
      return t('activity.mail.productionDeclarationRefuseSent');
    default:
      return t('activity.mail.sent');
  }
}

export function PurchaseOrderActivityMailSent({
  activity,
  t,
}: PurchaseOrderActivityMailSentProps) {
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const recipients = getMetadataStringArray(activity.metadata, 'recipients');
  const subject = getMetadataString(activity.metadata, 'subject');
  const message = getMetadataString(activity.metadata, 'message');

  return (
    <>
      <span className='font-semibold text-foreground'>
        {getMailSentLabel(activity.eventCode, t)}
      </span>
      {message ? (
        <button
          className='font-medium text-foreground underline underline-offset-2'
          onClick={() => setIsMessageOpen(true)}
          type='button'
        >
          {t('activity.mail.viewSentMessage')}
        </button>
      ) : null}
      {recipients.length > 0 ? (
        <span className='text-muted-foreground'>
          {t('activity.mail.sentTo', { recipients: recipients.join(', ') })}
        </span>
      ) : null}
      {message ? (
        <Dialog onOpenChange={setIsMessageOpen} open={isMessageOpen}>
          <DialogContent className='sm:max-w-2xl'>
            <DialogHeader>
              <DialogTitle>
                {subject ?? t('activity.mail.messageDialogTitle')}
              </DialogTitle>
            </DialogHeader>
            <pre className='max-h-[60vh] overflow-auto whitespace-pre-wrap font-sans text-foreground text-sm leading-6'>
              {message}
            </pre>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
